import React, { useId } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import Warning from 'design-system/src/assets/icons/warning.svg?react';
import { Checkbox, type CheckboxProps } from 'design-system/src/components/Checkbox/Checkbox';

import type { TremfyaExpressEnrollmentFormDataDefaultValues } from '../../../api';
import { SavingsProgramRequirements } from './SavingsProgramRequirements/SavingsProgramRequirements.tsx';
import { CustomHairline } from './sharedStyles.tsx';
import { requiredErrorMessage } from '../../../utils.tsx';
import { ControllerWrapper } from '../../legacySignup/ControllerWrapper.tsx';

const InlineButton = styled(Button)`
  display: inline-block;
`;

const ErrorBox = styled.div`
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: var(--border-radius-small);
  background: var(--extra-light-red);
`;

const ErrorBoxIcon = styled(Warning)`
  color: var(--red);
`;

const ErrorBoxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--dark-red);
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ConfirmEligibilitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EligibilityList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-inline-start: 16px;
  margin: 0;
`;

const tremfyaWithMeSavingsProgramUrl =
  'https://asset.janssencarepath.com/document/TREMFYA-withMe_Savings-Program-Web-Flashcard.pdf';

const programRequirementsLink = (
  <InlineButton variant="link" href={tremfyaWithMeSavingsProgramUrl} target="_blank" rel="noopener noreferrer">
    program requirements
  </InlineButton>
);

const ForwardedCheckbox = React.forwardRef<HTMLButtonElement, CheckboxProps>((props, ref) => (
  <Checkbox {...props} ref={ref} />
));
const StyledCheckbox = styled(ForwardedCheckbox)`
  margin: -9px 0 0 -9px !important;
  padding-right: 12px !important;
`;

export type TremfyaEnrollmentSavingsProgramSectionProps = {
  control: Control<TremfyaExpressEnrollmentFormDataDefaultValues>;
  errors: FieldErrors<TremfyaExpressEnrollmentFormDataDefaultValues>;
  isEligibleValue: 'Yes' | 'No' | null;
};

/**
 * The savings program section of the Tremfya express enrollment task.
 */
export const TremfyaEnrollmentSavingsProgramSection = ({
  control,
  errors,
  isEligibleValue,
}: TremfyaEnrollmentSavingsProgramSectionProps) => {
  return (
    <>
      <ConfirmEligibilitySection>
        <Typography styledAs="bodySmallDMSans" weight={600}>
          Confirm eligibility
        </Typography>
        <Typography styledAs="bodySmallDMSans">
          Eligible patients using commercial insurance pay as little as $0 per dose. Program provides two separate
          offerings: Medicine Cost Support for the cost of TREMFYA® medicine and Treatment Administration Cost Support
          for the cost of certain TREMFYA® administration and eligible laboratory tests. Maximum program benefit per
          calendar year shall apply. Offer subject to change or end without notice. Patients may participate without
          sharing their income information. See {programRequirementsLink}.
        </Typography>
        <Typography styledAs="bodySmallDMSans">
          Patients may be eligible for the TREMFYA withMe Savings Program if they meet the following requirements: *
        </Typography>
        <EligibilityList>
          <Typography styledAs="bodySmallDMSans" renderedAs="li">
            <strong>
              The patient has commercial or private health insurance that they will use for their TREMFYA® treatment.
            </strong>{' '}
            Examples are commercial insurance from a current/former employer, government employee health insurance, or
            insurance the patient buys privately or through the Health Insurance Marketplace (Healthcare.gov).
          </Typography>
          <Typography styledAs="bodySmallDMSans" renderedAs="li">
            <strong>
              The patient will NOT seek payment from any government-funded healthcare program for their TREMFYA®
              treatment.
            </strong>{' '}
            Examples are Medicare Parts A, B, C (also known as Medicare Advantage Plan), D, and Medicare Supplement,
            Medicaid, TRICARE, Department of Defense, or Veterans Administration.
          </Typography>
          <Typography styledAs="bodySmallDMSans" renderedAs="li" weight={600}>
            The patient will NOT submit any costs paid by this program as a claim to any health plan, patient assistance
            foundation, flexible spending account, or healthcare savings account.
          </Typography>
        </EligibilityList>
      </ConfirmEligibilitySection>

      <Controller
        control={control}
        name="is_eligible"
        rules={{ required: true }}
        render={({ field }) => (
          <RadioBlockGroup>
            <RadioOptionWrapper
              id={useId()}
              name="is_eligible"
              value="Yes"
              checked={field.value === 'Yes'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(event.target.value);
              }}
              hasError={!!errors.is_eligible}
            >
              <RadioLabel>Yes, I agree that the patient meets all of the requirements listed</RadioLabel>
            </RadioOptionWrapper>
            <RadioOptionWrapper
              id={useId()}
              name="is_eligible"
              value="No"
              checked={field.value === 'No'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(event.target.value);
              }}
              hasError={!!errors.is_eligible}
            >
              <RadioLabel>No, the patient does not meet one all of the requirements above</RadioLabel>
            </RadioOptionWrapper>
            {errors.is_eligible && requiredErrorMessage}
          </RadioBlockGroup>
        )}
      />

      {isEligibleValue === 'No' && (
        <ErrorBox>
          <ErrorBoxIcon />
          <ErrorBoxContent>
            <Typography styledAs="bodySmallDMSans" weight={600}>
              This program is not available to patients who are unable to meet the eligibility requirements listed.
            </Typography>
            <Typography styledAs="bodySmallDMSans">
              Questions about TREMFYA withMe support? Call a TREMFYA withMe Case Manager at:
            </Typography>
            <Typography styledAs="bodySmallDMSans">
              <strong>1-844-4withMe</strong> (844-494-8463)
            </Typography>
            <Typography styledAs="bodySmallDMSans">Monday - Friday, 8:00 AM - 8:00 PM ET</Typography>
          </ErrorBoxContent>
        </ErrorBox>
      )}

      {isEligibleValue === 'Yes' && (
        <>
          <CustomHairline />

          <Typography styledAs="bodySmallDMSans" weight={600}>
            Patient's Savings Program Terms and Conditions
          </Typography>
          <Typography styledAs="bodySmallDMSans">
            By confirming and submitting this information, I certify that I have completed all of the previous sections
            completely, accurately, and to the best of my knowledge, and that I have read, understand, and agree to the
            Terms and Conditions of the TREMFYA withMe Savings Program on behalf of the patient.
          </Typography>

          <SavingsProgramRequirements />

          <ControllerWrapper>
            <Controller
              control={control}
              name="patient_authorization"
              rules={{ required: true }}
              render={({ field }) => (
                <CheckboxContainer>
                  <StyledCheckbox
                    {...field}
                    checked={!!field.value}
                    onChange={() => field.onChange(!field.value)}
                    hasError={!!errors.patient_authorization}
                  />
                  <Typography styledAs="bodySmallDMSans">
                    By checking this box, I represent and warrant that I have on file written authorization from the
                    patient whose information I am submitting via this form to Johnson & Johnson Health Care Systems
                    Inc. and its affiliates and service providers (Janssen) that complies with state and federal law,
                    and the authorization enables Janssen to use and disclose the information as necessary to enroll the
                    patient in the TREMFYA withMe Savings Program.
                  </Typography>
                </CheckboxContainer>
              )}
            />
            {errors.patient_authorization && requiredErrorMessage}
          </ControllerWrapper>

          <ControllerWrapper>
            <Controller
              control={control}
              name="requirements_provided"
              rules={{ required: true }}
              render={({ field }) => (
                <CheckboxContainer>
                  <StyledCheckbox
                    {...field}
                    checked={!!field.value}
                    onChange={() => field.onChange(!field.value)}
                    hasError={!!errors.requirements_provided}
                  />
                  <Typography styledAs="bodySmallDMSans">
                    I acknowledge I have provided the patient with the {programRequirementsLink} for the TREMFYA withMe
                    Savings Program .
                  </Typography>
                </CheckboxContainer>
              )}
            />
            {errors.requirements_provided && requiredErrorMessage}
          </ControllerWrapper>
          <CustomHairline />
        </>
      )}
    </>
  );
};

export default TremfyaEnrollmentSavingsProgramSection;
